/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { CubismDefaultParameterId } from '@framework/cubismdefaultparameterid';
import { CubismModelSettingJson } from '@framework/cubismmodelsettingjson';
import {
  BreathParameterData,
  CubismBreath
} from '@framework/effect/cubismbreath';
import { CubismEyeBlink } from '@framework/effect/cubismeyeblink';
import { ICubismModelSetting } from '@framework/icubismmodelsetting';
import { CubismIdHandle } from '@framework/id/cubismid';
import { CubismFramework } from '@framework/live2dcubismframework';
import { CubismMatrix44 } from '@framework/math/cubismmatrix44';
import { CubismUserModel } from '@framework/model/cubismusermodel';
import {
  ACubismMotion,
  FinishedMotionCallback
} from '@framework/motion/acubismmotion';
import { CubismMotion } from '@framework/motion/cubismmotion';
import { CubismExpressionMotionManager } from '@framework/motion/cubismexpressionmotionmanager';
import {
  CubismMotionQueueEntryHandle,
  InvalidMotionQueueEntryHandleValue
} from '@framework/motion/cubismmotionqueuemanager';
import { csmMap } from '@framework/type/csmmap';
import { csmRect } from '@framework/type/csmrectf';
import { csmString } from '@framework/type/csmstring';
import { csmVector } from '@framework/type/csmvector';
import {
  CSM_ASSERT,
  CubismLogError,
  CubismLogInfo
} from '@framework/utils/cubismdebug';

import * as LAppDefine from './lappdefine';
import { frameBuffer, LAppDelegate } from './lappdelegate';
import { canvas, gl } from './lappglmanager';
import { LAppPal } from './lapppal';
import { TextureInfo } from './lapptexturemanager';
import { LAppWavFileHandler } from './lappwavfilehandler';
import { CubismMoc } from '@framework/model/cubismmoc';

// Global resolution tier for texture loading
let currentResolutionTier: string = 'high';

/**
 * Set the global resolution tier for all Live2D models
 * @param tier Resolution tier: 'low', 'medium', or 'high'
 */
export function setGlobalResolutionTier(tier: string): void {
  currentResolutionTier = tier;
  console.log(`[LAppModel] Global resolution tier set to: ${tier}`);
}

/**
 * Get the current global resolution tier
 * @returns Current resolution tier
 */
export function getGlobalResolutionTier(): string {
  return currentResolutionTier;
}

/**
 * Apply resolution tier to texture path
 * Converts paths like "model.8192/texture.png" to "model.2048/texture.png" based on tier
 * @param texturePath Original texture path from model3.json
 * @param tier Resolution tier: 'low', 'medium', or 'high'
 * @returns Modified texture path with appropriate resolution folder
 */
function applyResolutionTier(texturePath: string, tier: string): string {
  // Match resolution folder patterns like .8192/, .4096/, .2048/, .1024/, .512/
  const resolutionPattern = /\.(\d{3,4})\//;
  const match = texturePath.match(resolutionPattern);

  if (!match) {
    // No resolution folder found, return as-is
    return texturePath;
  }

  const originalRes = parseInt(match[1]);
  let targetRes: number;

  switch (tier) {
    case 'low':
      // Use 512 or 25% of original, whichever is reasonable
      targetRes = Math.max(512, Math.floor(originalRes * 0.25));
      break;
    case 'medium':
      // Use 1024-2048 or 50% of original
      targetRes = Math.max(1024, Math.floor(originalRes * 0.5));
      break;
    case 'high':
    default:
      // Use original resolution
      return texturePath;
  }

  // Round to nearest power of 2 or common texture size
  const commonSizes = [512, 1024, 2048, 4096, 8192];
  targetRes = commonSizes.reduce((prev, curr) =>
    Math.abs(curr - targetRes) < Math.abs(prev - targetRes) ? curr : prev
  );

  // Replace the resolution in the path
  const modifiedPath = texturePath.replace(resolutionPattern, `.${targetRes}/`);

  console.log(`[LAppModel] Texture path resolution: ${tier} tier -> ${originalRes} => ${targetRes}`);
  console.log(`[LAppModel]   Original: ${texturePath}`);
  console.log(`[LAppModel]   Modified: ${modifiedPath}`);

  return modifiedPath;
}

export enum LoadStep {
  LoadAssets,
  LoadModel,
  WaitLoadModel,
  LoadPriorityExpressions,
  WaitLoadPriorityExpressions,
  LoadExpression,
  WaitLoadExpression,
  LoadPhysics,
  WaitLoadPhysics,
  LoadPose,
  WaitLoadPose,
  SetupEyeBlink,
  SetupBreath,
  LoadUserData,
  WaitLoadUserData,
  SetupEyeBlinkIds,
  SetupLipSyncIds,
  SetupLayout,
  LoadMotion,
  WaitLoadMotion,
  CompleteInitialize,
  CompleteSetupModel,
  LoadTexture,
  WaitLoadTexture,
  CompleteSetup
}

/**
 * チャンネル表情システムのインターフェース
 */
interface ChannelExpressionState {
  expressionName: string;           // 現在再生中の表情名
  motion: ACubismMotion;           // 表情モーション
  manager: CubismExpressionMotionManager; // 表情管理マネージャー
  duration: number;                // 持続時間（-1は永続）
  startTime: number;               // 開始時刻
  fadingOut: boolean;              // フェードアウト中かどうか
  fadeOutStartTime: number;        // フェードアウト開始時刻
  fadeOutDuration: number;         // フェードアウト時間
}

interface ExpressionLoadFailure {
  filePath: string;
  reason: string;
}

interface ExpressionLoadSummary {
  expectedNames: string[];
  loadedNames: string[];
  failed: { name: string; filePath: string; reason: string }[];
  pendingNames: string[];
  pendingDetails: { name: string; filePath: string | null }[];
}

/**
 * ユーザーが実際に使用するモデルの実装クラス<br>
 * モデル生成、機能コンポーネント生成、更新処理とレンダリングの呼び出しを行う。
 */
export class LAppModel extends CubismUserModel {
  /**
   * model3.jsonが置かれたディレクトリとファイルパスからモデルを生成する
   * @param dir
   * @param fileName
   */
  public loadAssets(dir: string, fileName: string): void {
    this._modelHomeDir = dir;
    this._expressionCount = 0;
    this._loadedExpressionNames.clear();
    this._failedExpressionNames.clear();
    this._expressionFileMap.clear();
    this._priorityExpressionNames.clear();
    this._priorityExpressionsLoaded = false;
    this._loadStartTime = performance.now();
    this._priorityLoadStartTime = 0;

    fetch(`${this._modelHomeDir}${fileName}`)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => {
        const setting: ICubismModelSetting = new CubismModelSettingJson(
          arrayBuffer,
          arrayBuffer.byteLength
        );

        // ステートを更新
        this._state = LoadStep.LoadModel;

        // 結果を保存
        this.setupModel(setting);
      })
      .catch(error => {
        // model3.json読み込みでエラーが発生した時点で描画は不可能なので、setupせずエラーをcatchして何もしない
        CubismLogError(`Failed to load file ${this._modelHomeDir}${fileName}`);
      });
  }

  public getExpressionLoadSummary(): ExpressionLoadSummary {
    const expectedNames: string[] = [];
    const baseDir = this._modelHomeDir || '';

    if (this._modelSetting) {
      const count = this._modelSetting.getExpressionCount();
      for (let i = 0; i < count; i++) {
        const name = this._modelSetting.getExpressionName(i);
        const fileName = this._modelSetting.getExpressionFileName(i);
        expectedNames.push(name);

        if (!this._expressionFileMap.has(name)) {
          this._expressionFileMap.set(name, `${baseDir}${fileName}`);
        }
      }
    }

    const loadedNames = Array.from(this._loadedExpressionNames);
    const failed: { name: string; filePath: string; reason: string }[] = [];

    this._failedExpressionNames.forEach((info, name) => {
      failed.push({
        name,
        filePath: info.filePath,
        reason: info.reason
      });
    });

    const pendingNames = expectedNames.filter(
      name =>
        !this._loadedExpressionNames.has(name) &&
        !this._failedExpressionNames.has(name)
    );

    const pendingDetails = pendingNames.map(name => ({
      name,
      filePath: this._expressionFileMap.has(name)
        ? this._expressionFileMap.get(name) || null
        : null
    }));

    return {
      expectedNames,
      loadedNames,
      failed,
      pendingNames,
      pendingDetails
    };
  }

  /**
   * Determine which expressions should be loaded with priority based on pending wardrobe data
   */
  private _determinePriorityExpressions(): void {
    this._priorityExpressionNames.clear();

    // Check if we have pending wardrobe expressions from Flutter
    const pendingWardrobe = (window as any).__Live2DPendingWardrobeExpressions;

    if (!pendingWardrobe || typeof pendingWardrobe !== 'object') {
      console.log('[LAppModel] No pending wardrobe expressions found');
      return;
    }

    // Collect all unique expression names from all channels
    const prioritySet = new Set<string>();

    for (const channel in pendingWardrobe) {
      const expressionList = pendingWardrobe[channel];
      if (Array.isArray(expressionList)) {
        expressionList.forEach(expr => {
          if (typeof expr === 'string' && expr.trim() !== '') {
            prioritySet.add(expr);
          }
        });
      }
    }

    // Verify these expressions exist in the model
    const count = this._modelSetting.getExpressionCount();
    for (let i = 0; i < count; i++) {
      const expressionName = this._modelSetting.getExpressionName(i);
      if (prioritySet.has(expressionName)) {
        this._priorityExpressionNames.add(expressionName);
      }
    }

    console.log(
      `[LAppModel] Identified ${this._priorityExpressionNames.size} priority expressions from wardrobe:`,
      Array.from(this._priorityExpressionNames)
    );
  }

  /**
   * model3.jsonからモデルを生成する。
   * model3.jsonの記述に従ってモデル生成、モーション、物理演算などのコンポーネント生成を行う。
   *
   * @param setting ICubismModelSettingのインスタンス
   */
  private setupModel(setting: ICubismModelSetting): void {
    this._updating = true;
    this._initialized = false;

    this._modelSetting = setting;

    // CubismModel
    if (this._modelSetting.getModelFileName() != '') {
      const modelFileName = this._modelSetting.getModelFileName();

      fetch(`${this._modelHomeDir}${modelFileName}`)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else if (response.status >= 400) {
            CubismLogError(
              `Failed to load file ${this._modelHomeDir}${modelFileName}`
            );
            return new ArrayBuffer(0);
          }
        })
        .then(arrayBuffer => {
          this.loadModel(arrayBuffer, this._mocConsistency);

          // Pre-populate expression file map for all expressions
          const count = this._modelSetting.getExpressionCount();
          for (let i = 0; i < count; i++) {
            const expressionName = this._modelSetting.getExpressionName(i);
            const expressionFileName = this._modelSetting.getExpressionFileName(i);
            const expressionPath = `${this._modelHomeDir}${expressionFileName}`;
            this._expressionFileMap.set(expressionName, expressionPath);
          }

          // Check if we have priority expressions to load first
          this._determinePriorityExpressions();

          if (this._priorityExpressionNames.size > 0) {
            this._state = LoadStep.LoadPriorityExpressions;
            loadPriorityExpressions();
          } else {
            // No priority expressions, load all normally
            this._state = LoadStep.LoadExpression;
            loadCubismExpression();
          }
        });

      this._state = LoadStep.WaitLoadModel;
    } else {
      LAppPal.printMessage('Model data does not exist.');
    }

    // Priority Expressions (Wardrobe expressions to load first)
    const loadPriorityExpressions = (): void => {
      if (this._priorityExpressionNames.size === 0) {
        const elapsed = Math.round(performance.now() - this._loadStartTime);
        console.log(`[LAppModel] No priority expressions to load, proceeding to physics (+${elapsed}ms)`);
        this._priorityExpressionsLoaded = true;
        this._state = LoadStep.LoadPhysics;
        loadCubismPhysics();
        return;
      }

      this._priorityLoadStartTime = performance.now();
      const elapsed = Math.round(this._priorityLoadStartTime - this._loadStartTime);
      console.log(`[LAppModel] Loading ${this._priorityExpressionNames.size} priority expressions first (+${elapsed}ms)`);
      const priorityList = Array.from(this._priorityExpressionNames);
      let priorityLoadedCount = 0;

      for (const expressionName of priorityList) {
        const expressionPath = this._expressionFileMap.get(expressionName);
        if (!expressionPath) {
          console.warn(`[LAppModel] Priority expression '${expressionName}' not found in expression file map`);
          priorityLoadedCount++;
          continue;
        }

        const finalizePriorityLoad = () => {
          priorityLoadedCount++;

          if (priorityLoadedCount >= priorityList.length) {
            this._priorityExpressionsLoaded = true;
            const totalElapsed = Math.round(performance.now() - this._loadStartTime);
            const priorityDuration = Math.round(performance.now() - this._priorityLoadStartTime);
            console.log(`[LAppModel] Priority expressions loaded (${this._loadedExpressionNames.size}/${priorityList.length} successful) in ${priorityDuration}ms (+${totalElapsed}ms total)`);

            // Move to physics loading to allow model display
            this._state = LoadStep.LoadPhysics;
            loadCubismPhysics();

            // Start loading remaining expressions in background after a short delay
            setTimeout(() => {
              loadRemainingExpressions();
            }, 100);
          }
        };

        fetch(expressionPath)
          .then(response => {
            if (response && response.ok) {
              return response.arrayBuffer();
            }

            const status = response ? response.status : 'unknown';
            const statusText = response ? response.statusText || '' : '';
            const reason =
              typeof status === 'number'
                ? `HTTP ${status}${statusText ? ` (${statusText})` : ''}`
                : 'Unknown response error';

            console.error(
              `[LAppModel] Priority expression '${expressionName}' failed to load (${reason})`
            );

            this._failedExpressionNames.set(expressionName, {
              filePath: expressionPath,
              reason
            });

            return new ArrayBuffer(0);
          })
          .then(arrayBuffer => {
            try {
              if (!arrayBuffer || arrayBuffer.byteLength === 0) {
                return;
              }

              const motion: ACubismMotion = this.loadExpression(
                arrayBuffer,
                arrayBuffer.byteLength,
                expressionName
              );

              if (this._expressions.getValue(expressionName) != null) {
                ACubismMotion.delete(
                  this._expressions.getValue(expressionName)
                );
                this._expressions.setValue(expressionName, null);
              }

              this._expressions.setValue(expressionName, motion);

              if (motion) {
                this._loadedExpressionNames.add(expressionName);
                this._failedExpressionNames.delete(expressionName);
              } else if (!this._failedExpressionNames.has(expressionName)) {
                this._failedExpressionNames.set(expressionName, {
                  filePath: expressionPath,
                  reason: 'Expression data could not be parsed'
                });
              }
            } finally {
              finalizePriorityLoad();
            }
          })
          .catch(error => {
            const reason =
              error instanceof Error ? error.message : String(error);
            console.error(
              `[LAppModel] Exception loading priority expression '${expressionName}':`,
              error
            );
            this._failedExpressionNames.set(expressionName, {
              filePath: expressionPath,
              reason
            });
            finalizePriorityLoad();
          });
      }

      this._state = LoadStep.WaitLoadPriorityExpressions;
    };

    // Remaining Expressions (Background loading of non-priority expressions)
    const loadRemainingExpressions = (): void => {
      const backgroundStartTime = performance.now();
      const count = this._modelSetting.getExpressionCount();
      const remainingExpressions: string[] = [];

      for (let i = 0; i < count; i++) {
        const expressionName = this._modelSetting.getExpressionName(i);
        if (!this._priorityExpressionNames.has(expressionName) &&
            !this._loadedExpressionNames.has(expressionName)) {
          remainingExpressions.push(expressionName);
        }
      }

      if (remainingExpressions.length === 0) {
        const elapsed = Math.round(backgroundStartTime - this._loadStartTime);
        console.log(`[LAppModel] No remaining expressions to load (+${elapsed}ms)`);
        return;
      }

      const elapsed = Math.round(backgroundStartTime - this._loadStartTime);
      console.log(`[LAppModel] Loading ${remainingExpressions.length} remaining expressions in background (+${elapsed}ms)`);

      for (const expressionName of remainingExpressions) {
        const expressionPath = this._expressionFileMap.get(expressionName);
        if (!expressionPath) {
          console.warn(`[LAppModel] Remaining expression '${expressionName}' not found in file map`);
          continue;
        }

        fetch(expressionPath)
          .then(response => {
            if (response && response.ok) {
              return response.arrayBuffer();
            }

            const status = response ? response.status : 'unknown';
            const statusText = response ? response.statusText || '' : '';
            const reason =
              typeof status === 'number'
                ? `HTTP ${status}${statusText ? ` (${statusText})` : ''}`
                : 'Unknown response error';

            this._failedExpressionNames.set(expressionName, {
              filePath: expressionPath,
              reason
            });

            return new ArrayBuffer(0);
          })
          .then(arrayBuffer => {
            if (!arrayBuffer || arrayBuffer.byteLength === 0) {
              return;
            }

            const motion: ACubismMotion = this.loadExpression(
              arrayBuffer,
              arrayBuffer.byteLength,
              expressionName
            );

            if (this._expressions.getValue(expressionName) != null) {
              ACubismMotion.delete(
                this._expressions.getValue(expressionName)
              );
              this._expressions.setValue(expressionName, null);
            }

            this._expressions.setValue(expressionName, motion);

            if (motion) {
              this._loadedExpressionNames.add(expressionName);
              this._failedExpressionNames.delete(expressionName);
            } else if (!this._failedExpressionNames.has(expressionName)) {
              this._failedExpressionNames.set(expressionName, {
                filePath: expressionPath,
                reason: 'Expression data could not be parsed'
              });
            }
          })
          .catch(error => {
            const reason =
              error instanceof Error ? error.message : String(error);
            this._failedExpressionNames.set(expressionName, {
              filePath: expressionPath,
              reason
            });
          });
      }
    };

    // Expression (Legacy full load - kept for fallback)
    const loadCubismExpression = (): void => {
      if (this._modelSetting.getExpressionCount() > 0) {
        const count: number = this._modelSetting.getExpressionCount();

        for (let i = 0; i < count; i++) {
          const expressionName = this._modelSetting.getExpressionName(i);
          const expressionFileName =
            this._modelSetting.getExpressionFileName(i);

          const expressionPath = `${this._modelHomeDir}${expressionFileName}`;
          this._expressionFileMap.set(expressionName, expressionPath);
          this._loadedExpressionNames.delete(expressionName);
          this._failedExpressionNames.delete(expressionName);

          const finalizeExpressionLoad = () => {
            this._expressionCount++;

            if (this._expressionCount >= count) {
              this._state = LoadStep.LoadPhysics;

              // callback
              loadCubismPhysics();
            }
          };

          fetch(expressionPath)
            .then(response => {
              if (response && response.ok) {
                return response.arrayBuffer();
              }

              const status = response ? response.status : 'unknown';
              const statusText = response ? response.statusText || '' : '';
              const reason =
                typeof status === 'number'
                  ? `HTTP ${status}${statusText ? ` (${statusText})` : ''}`
                  : 'Unknown response error';

              CubismLogError(`Failed to load file ${expressionPath}`);
              console.error(
                `[LAppModel] Expression '${expressionName}' failed to load (${reason}) from ${expressionPath}`
              );

              this._failedExpressionNames.set(expressionName, {
                filePath: expressionPath,
                reason
              });

              return new ArrayBuffer(0);
            })
            .then(arrayBuffer => {
              try {
                if (!arrayBuffer) {
                  return;
                }

                const motion: ACubismMotion = this.loadExpression(
                  arrayBuffer,
                  arrayBuffer.byteLength,
                  expressionName
                );

                if (this._expressions.getValue(expressionName) != null) {
                  ACubismMotion.delete(
                    this._expressions.getValue(expressionName)
                  );
                  this._expressions.setValue(expressionName, null);
                }

                this._expressions.setValue(expressionName, motion);

                if (motion) {
                  this._loadedExpressionNames.add(expressionName);
                  this._failedExpressionNames.delete(expressionName);
                } else if (!this._failedExpressionNames.has(expressionName)) {
                  this._failedExpressionNames.set(expressionName, {
                    filePath: expressionPath,
                    reason: 'Expression data could not be parsed'
                  });
                }
              } finally {
                finalizeExpressionLoad();
              }
            })
            .catch(error => {
              const reason =
                error instanceof Error ? error.message : String(error);
              console.error(
                `[LAppModel] Exception while loading expression '${expressionName}' from ${expressionPath}:`,
                error
              );
              this._failedExpressionNames.set(expressionName, {
                filePath: expressionPath,
                reason
              });
              finalizeExpressionLoad();
            });
        }
        this._state = LoadStep.WaitLoadExpression;
      } else {
        this._state = LoadStep.LoadPhysics;

        // callback
        loadCubismPhysics();
      }
    };

    // Physics
    const loadCubismPhysics = (): void => {
      if (this._modelSetting.getPhysicsFileName() != '') {
        const physicsFileName = this._modelSetting.getPhysicsFileName();

        fetch(`${this._modelHomeDir}${physicsFileName}`)
          .then(response => {
            if (response.ok) {
              return response.arrayBuffer();
            } else if (response.status >= 400) {
              CubismLogError(
                `Failed to load file ${this._modelHomeDir}${physicsFileName}`
              );
              return new ArrayBuffer(0);
            }
          })
          .then(arrayBuffer => {
            this.loadPhysics(arrayBuffer, arrayBuffer.byteLength);

            this._state = LoadStep.LoadPose;

            // callback
            loadCubismPose();
          });
        this._state = LoadStep.WaitLoadPhysics;
      } else {
        this._state = LoadStep.LoadPose;

        // callback
        loadCubismPose();
      }
    };

    // Pose
    const loadCubismPose = (): void => {
      if (this._modelSetting.getPoseFileName() != '') {
        const poseFileName = this._modelSetting.getPoseFileName();

        fetch(`${this._modelHomeDir}${poseFileName}`)
          .then(response => {
            if (response.ok) {
              return response.arrayBuffer();
            } else if (response.status >= 400) {
              CubismLogError(
                `Failed to load file ${this._modelHomeDir}${poseFileName}`
              );
              return new ArrayBuffer(0);
            }
          })
          .then(arrayBuffer => {
            this.loadPose(arrayBuffer, arrayBuffer.byteLength);

            this._state = LoadStep.SetupEyeBlink;

            // callback
            setupEyeBlink();
          });
        this._state = LoadStep.WaitLoadPose;
      } else {
        this._state = LoadStep.SetupEyeBlink;

        // callback
        setupEyeBlink();
      }
    };

    // EyeBlink
    const setupEyeBlink = (): void => {
      if (this._modelSetting.getEyeBlinkParameterCount() > 0) {
        this._eyeBlink = CubismEyeBlink.create(this._modelSetting);
        this._state = LoadStep.SetupBreath;
      }

      // callback
      setupBreath();
    };

    // Breath
    const setupBreath = (): void => {
      this._breath = CubismBreath.create();

      const breathParameters: csmVector<BreathParameterData> = new csmVector();
      breathParameters.pushBack(
        new BreathParameterData(this._idParamAngleX, 0.0, 15.0, 6.5345, 0.5)
      );
      breathParameters.pushBack(
        new BreathParameterData(this._idParamAngleY, 0.0, 8.0, 3.5345, 0.5)
      );
      breathParameters.pushBack(
        new BreathParameterData(this._idParamAngleZ, 0.0, 10.0, 5.5345, 0.5)
      );
      breathParameters.pushBack(
        new BreathParameterData(this._idParamBodyAngleX, 0.0, 4.0, 15.5345, 0.5)
      );
      breathParameters.pushBack(
        new BreathParameterData(
          CubismFramework.getIdManager().getId(
            CubismDefaultParameterId.ParamBreath
          ),
          0.5,
          0.5,
          3.2345,
          1
        )
      );

      this._breath.setParameters(breathParameters);
      this._state = LoadStep.LoadUserData;

      // callback
      loadUserData();
    };

    // UserData
    const loadUserData = (): void => {
      if (this._modelSetting.getUserDataFile() != '') {
        const userDataFile = this._modelSetting.getUserDataFile();

        fetch(`${this._modelHomeDir}${userDataFile}`)
          .then(response => {
            if (response.ok) {
              return response.arrayBuffer();
            } else if (response.status >= 400) {
              CubismLogError(
                `Failed to load file ${this._modelHomeDir}${userDataFile}`
              );
              return new ArrayBuffer(0);
            }
          })
          .then(arrayBuffer => {
            this.loadUserData(arrayBuffer, arrayBuffer.byteLength);

            this._state = LoadStep.SetupEyeBlinkIds;

            // callback
            setupEyeBlinkIds();
          });

        this._state = LoadStep.WaitLoadUserData;
      } else {
        this._state = LoadStep.SetupEyeBlinkIds;

        // callback
        setupEyeBlinkIds();
      }
    };

    // EyeBlinkIds
    const setupEyeBlinkIds = (): void => {
      const eyeBlinkIdCount: number =
        this._modelSetting.getEyeBlinkParameterCount();

      for (let i = 0; i < eyeBlinkIdCount; ++i) {
        this._eyeBlinkIds.pushBack(
          this._modelSetting.getEyeBlinkParameterId(i)
        );
      }

      this._state = LoadStep.SetupLipSyncIds;

      // callback
      setupLipSyncIds();
    };

    // LipSyncIds
    const setupLipSyncIds = (): void => {
      const lipSyncIdCount = this._modelSetting.getLipSyncParameterCount();

      for (let i = 0; i < lipSyncIdCount; ++i) {
        this._lipSyncIds.pushBack(this._modelSetting.getLipSyncParameterId(i));
      }
      this._state = LoadStep.SetupLayout;

      // callback
      setupLayout();
    };

    // Layout
    const setupLayout = (): void => {
      const layout: csmMap<string, number> = new csmMap<string, number>();

      if (this._modelSetting == null || this._modelMatrix == null) {
        CubismLogError('Failed to setupLayout().');
        return;
      }

      this._modelSetting.getLayoutMap(layout);
      this._modelMatrix.setupFromLayout(layout);
      this._captureBaseModelMatrix();
      this.setModelYOffset(LAppDefine.ModelInitialYOffset);
      this._state = LoadStep.LoadMotion;

      // callback
      loadCubismMotion();
    };

    // Motion
    const loadCubismMotion = (): void => {
      this._state = LoadStep.WaitLoadMotion;
      this._model.saveParameters();
      this._allMotionCount = 0;
      this._motionCount = 0;
      const group: string[] = [];

      const motionGroupCount: number = this._modelSetting.getMotionGroupCount();

      // モーションの総数を求める
      for (let i = 0; i < motionGroupCount; i++) {
        group[i] = this._modelSetting.getMotionGroupName(i);
        this._allMotionCount += this._modelSetting.getMotionCount(group[i]);
      }

      // モーションの読み込み
      for (let i = 0; i < motionGroupCount; i++) {
        this.preLoadMotionGroup(group[i]);
      }

      // モーションがない場合
      if (motionGroupCount == 0) {
        this._state = LoadStep.LoadTexture;

        // 全てのモーションを停止する
        this._motionManager.stopAllMotions();

        this._updating = false;
        this._initialized = true;

        this.createRenderer();
        this.setupTextures();
        this.getRenderer().startUp(gl);
      }
    };
  }

  /**
   * テクスチャユニットにテクスチャをロードする
   */
  private setupTextures(): void {
    // iPhoneでのアルファ品質向上のためTypescriptではpremultipliedAlphaを採用
    const usePremultiply = true;

    if (this._state == LoadStep.LoadTexture) {
      // テクスチャ読み込み用
      const textureCount: number = this._modelSetting.getTextureCount();

      for (
        let modelTextureNumber = 0;
        modelTextureNumber < textureCount;
        modelTextureNumber++
      ) {
        // テクスチャ名が空文字だった場合はロード・バインド処理をスキップ
        if (this._modelSetting.getTextureFileName(modelTextureNumber) == '') {
          console.log('getTextureFileName null');
          continue;
        }

        // WebGLのテクスチャユニットにテクスチャをロードする
        let texturePath =
          this._modelSetting.getTextureFileName(modelTextureNumber);

        // Apply resolution tier to texture path
        texturePath = applyResolutionTier(texturePath, currentResolutionTier);

        texturePath = this._modelHomeDir + texturePath;

        // ロード完了時に呼び出すコールバック関数
        const onLoad = (textureInfo: TextureInfo): void => {
          this.getRenderer().bindTexture(modelTextureNumber, textureInfo.id);

          this._textureCount++;

          if (this._textureCount >= textureCount) {
            // ロード完了
            this._state = LoadStep.CompleteSetup;
          }
        };

        // 読み込み
        LAppDelegate.getInstance()
          .getTextureManager()
          .createTextureFromPngFile(texturePath, usePremultiply, onLoad);
        this.getRenderer().setIsPremultipliedAlpha(usePremultiply);
      }

      this._state = LoadStep.WaitLoadTexture;
    }
  }

  /**
   * レンダラを再構築する
   */
  public reloadRenderer(): void {
    this.deleteRenderer();
    this.createRenderer();
    this.setupTextures();
  }

  /**
   * Check if the model has completed the entire loading pipeline, including textures.
   */
  public isFullyInitialized(): boolean {
    return this._state === LoadStep.CompleteSetup;
  }

  /**
   * Get the current load state for diagnostics.
   */
  public getLoadState(): LoadStep {
    return this._state;
  }

  /**
   * 更新
   */
  public update(): void {
    if (this._state != LoadStep.CompleteSetup) return;

    const deltaTimeSeconds: number = LAppPal.getDeltaTime();
    this._userTimeSeconds += deltaTimeSeconds;

    this._updateModelXOffset(deltaTimeSeconds);
    this._updateModelYOffset(deltaTimeSeconds);

    this._dragManager.update(deltaTimeSeconds);
    this._dragX = this._dragManager.getX();
    this._dragY = this._dragManager.getY();

    // モーションによるパラメータ更新の有無
    let motionUpdated = false;

    //--------------------------------------------------------------------------
    this._model.loadParameters(); // 前回セーブされた状態をロード
    if (this._motionManager.isFinished()) {
      // モーションの再生がない場合、待機モーションの中からランダムで再生する
      this.startRandomMotion(
        LAppDefine.MotionGroupIdle,
        LAppDefine.PriorityIdle
      );
    } else {
      motionUpdated = this._motionManager.updateMotion(
        this._model,
        deltaTimeSeconds
      ); // モーションを更新
    }
    this._model.saveParameters(); // 状態を保存
    //--------------------------------------------------------------------------

    // まばたき
    if (!motionUpdated) {
      if (this._eyeBlink != null) {
        // メインモーションの更新がないとき
        this._eyeBlink.updateParameters(this._model, deltaTimeSeconds); // 目パチ
      }
    }

    if (this._expressionManager != null) {
      this._expressionManager.updateMotion(this._model, deltaTimeSeconds); // 表情でパラメータ更新（相対変化）
    }

    // Update channel-based expressions
    this.updateChannelExpressions(deltaTimeSeconds);

    // ドラッグによる変化
    // ドラッグによる顔の向きの調整
    this._model.addParameterValueById(this._idParamAngleX, this._dragX * 30); // -30から30の値を加える
    this._model.addParameterValueById(this._idParamAngleY, this._dragY * 30);
    this._model.addParameterValueById(
      this._idParamAngleZ,
      this._dragX * this._dragY * -30
    );

    // ドラッグによる体の向きの調整
    this._model.addParameterValueById(
      this._idParamBodyAngleX,
      this._dragX * 10
    ); // -10から10の値を加える

    // ドラッグによる目の向きの調整
    this._model.addParameterValueById(this._idParamEyeBallX, this._dragX); // -1から1の値を加える
    this._model.addParameterValueById(this._idParamEyeBallY, this._dragY);

    // 呼吸など
    if (this._breath != null) {
      this._breath.updateParameters(this._model, deltaTimeSeconds);
    }

    // 物理演算の設定
    if (this._physics != null) {
      this._physics.evaluate(this._model, deltaTimeSeconds);
    }

    // リップシンクの設定
    if (this._isTalking) {
      // 話しアニメーション中は自動口パク制御
      this._talkingTime += deltaTimeSeconds;
      this._vowelChangeTime += deltaTimeSeconds;

      // 母音を定期的に変更
      if (this._vowelChangeTime >= this._vowelDuration) {
        const vowels = ['A', 'E', 'I', 'O', 'U'];
        this._currentVowel = vowels[Math.floor(Math.random() * vowels.length)];
        this._vowelChangeTime = 0.0;
        // ランダムな継続時間 (0.15-0.4秒)
        this._vowelDuration = 0.15 + Math.random() * 0.25;
      }

      // より自然な話し方のために複数の波を組み合わせ
      const baseWave = Math.sin(this._talkingTime * this._talkingSpeed * 2 * Math.PI);
      const secondaryWave = Math.sin(this._talkingTime * this._talkingSpeed * 3.2 * Math.PI) * 0.3;
      const tertiaryWave = Math.sin(this._talkingTime * this._talkingSpeed * 1.7 * Math.PI) * 0.2;

      // 組み合わせた波形
      const combinedWave = baseWave + secondaryWave + tertiaryWave;

      // ランダム要素を追加（より自然な動きのため）
      const randomVariation = (Math.random() - 0.5) * 0.15;
      const speedVariation = 1.0 + (Math.random() - 0.5) * 0.2;

      // より自然な話し方のために微妙な変化を追加
      const naturalVariation = Math.sin(this._talkingTime * 0.3) * 0.15 + 0.85;

      // 基本的な口の開閉値を計算（より大きな値を生成するように調整）
      let baseIntensity = ((combinedWave * speedVariation + randomVariation) * this._talkingIntensity * naturalVariation);
      // 適切な開閉範囲を確保（0-1の範囲で、しっかり閉じてしっかり開く）
      baseIntensity = Math.max(0.0, Math.min(1.0, (baseIntensity + 1.0) * 0.6));

      // より滑らかな動きのためにイージング適用
      baseIntensity = baseIntensity * baseIntensity * (3.0 - 2.0 * baseIntensity);

      // 現在の母音に基づいて口の形状を調整
      const vowelParams = this.getVowelMouthParameters(this._currentVowel, baseIntensity);


      // 複数の口パラメータに適用
      this.applyMouthParameters(vowelParams);
    } else if (this._isClosingMouth) {
      // 口を徐々に閉じるアニメーション
      this._mouthCloseTime += deltaTimeSeconds;
      const closeDuration = 0.3; // 0.3秒かけて閉じる

      if (this._mouthCloseTime >= closeDuration) {
        // アニメーション完了
        this._isClosingMouth = false;
        this._mouthCloseTime = 0.0;

        // 完全に口を閉じる
        const closedParams = {
          mouthOpenY: 0.0,
          mouthForm: 0.0,
          mouthWidth: 0.0
        };
        this.applyMouthParameters(closedParams);

        // 元のリップシンク状態に戻す
        this._lipsync = this._originalLipSyncState;
        console.log("Mouth closing animation completed, restored lip sync:", this._originalLipSyncState);
      } else {
        // 徐々に口を閉じる
        const progress = this._mouthCloseTime / closeDuration;
        const easeOutProgress = 1.0 - Math.pow(1.0 - progress, 3); // ease-out cubic
        const mouthValue = (1.0 - easeOutProgress) * 0.3; // 現在の口の開き具合から0に向かって減少

        const closingParams = {
          mouthOpenY: mouthValue,
          mouthForm: (1.0 - easeOutProgress) * 0.1, // 形状も徐々にニュートラルに
          mouthWidth: (1.0 - easeOutProgress) * 0.1
        };
        this.applyMouthParameters(closingParams);

        console.log("Closing mouth progress:", (progress * 100).toFixed(1) + "%", "mouth value:", mouthValue.toFixed(3));
      }
    } else if (this._lipsync) {
      let value = 0.0; // リアルタイムでリップシンクを行う場合、システムから音量を取得して、0~1の範囲で値を入力します。

      this._wavFileHandler.update(deltaTimeSeconds);
      value = this._wavFileHandler.getRms();

      for (let i = 0; i < this._lipSyncIds.getSize(); ++i) {
        this._model.addParameterValueById(this._lipSyncIds.at(i), value, 0.8);
      }
    }

    // ポーズの設定
    if (this._pose != null) {
      this._pose.updateParameters(this._model, deltaTimeSeconds);
    }

    this._model.update();
  }

  /**
   * 引数で指定したモーションの再生を開始する
   * @param group モーショングループ名
   * @param no グループ内の番号
   * @param priority 優先度
   * @param onFinishedMotionHandler モーション再生終了時に呼び出されるコールバック関数
   * @return 開始したモーションの識別番号を返す。個別のモーションが終了したか否かを判定するisFinished()の引数で使用する。開始できない時は[-1]
   */
  public playMotion(
    group: string,
    no: number,
    priority: number,
    onFinishedMotionHandler?: FinishedMotionCallback
  ): CubismMotionQueueEntryHandle {
    if (priority == LAppDefine.PriorityForce) {
      this._motionManager.setReservePriority(priority);
    } else if (!this._motionManager.reserveMotion(priority)) {
      if (this._debugMode) {
        LAppPal.printMessage("[APP]can't start motion.");
      }
      return InvalidMotionQueueEntryHandleValue;
    }

    const motionFileName = this._modelSetting.getMotionFileName(group, no);

    // ex) idle_0
    const name = `${group}_${no}`;
    let motion: CubismMotion = this._motions.getValue(name) as CubismMotion;
    let autoDelete = false;

    if (motion == null) {
      fetch(`${this._modelHomeDir}${motionFileName}`)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else if (response.status >= 400) {
            CubismLogError(
              `Failed to load file ${this._modelHomeDir}${motionFileName}`
            );
            return new ArrayBuffer(0);
          }
        })
        .then(arrayBuffer => {
          motion = this.loadMotion(
            arrayBuffer,
            arrayBuffer.byteLength,
            null,
            onFinishedMotionHandler
          );

          if (motion == null) {
            return;
          }

          let fadeTime: number = this._modelSetting.getMotionFadeInTimeValue(
            group,
            no
          );

          if (fadeTime >= 0.0) {
            motion.setFadeInTime(fadeTime);
          }

          fadeTime = this._modelSetting.getMotionFadeOutTimeValue(group, no);
          if (fadeTime >= 0.0) {
            motion.setFadeOutTime(fadeTime);
          }

          motion.setEffectIds(this._eyeBlinkIds, this._lipSyncIds);
          autoDelete = true; // 終了時にメモリから削除
        });
    } else {
      motion.setFinishedMotionHandler(onFinishedMotionHandler);
    }

    //voice
    const voice = this._modelSetting.getMotionSoundFileName(group, no);
    if (voice.localeCompare('') != 0) {
      let path = voice;
      path = this._modelHomeDir + path;
      this._wavFileHandler.start(path);
    }

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]start motion: [${group}_${no}`);
    }
    return this._motionManager.startMotionPriority(
      motion,
      autoDelete,
      priority
    );
  }

  /**
   * ファイル名からモーションのグループとインデックスを検索する
   * @param fileName モーションファイル名（例: "Idle_00.motion3.json" または "Idle_00"）
   * @return {group: string, index: number} | null
   */
  public findMotionByFileName(fileName: string): { group: string; index: number } | null {
    // .motion3.json 拡張子を削除
    const cleanFileName = fileName.replace('.motion3.json', '');

    const motionGroupCount = this._modelSetting.getMotionGroupCount();

    // First, try exact match in model settings
    for (let i = 0; i < motionGroupCount; i++) {
      const groupName = this._modelSetting.getMotionGroupName(i);
      const motionCount = this._modelSetting.getMotionCount(groupName);

      for (let j = 0; j < motionCount; j++) {
        const motionFileName = this._modelSetting.getMotionFileName(groupName, j);
        const cleanMotionFileName = motionFileName.replace('.motion3.json', '');

        if (cleanMotionFileName === cleanFileName || motionFileName === fileName) {
          return { group: groupName, index: j };
        }
      }
    }

    // If not found, try auto-detection by checking if filename contains path parts
    // Check for patterns like "motions/..." in the motion files
    for (let i = 0; i < motionGroupCount; i++) {
      const groupName = this._modelSetting.getMotionGroupName(i);
      const motionCount = this._modelSetting.getMotionCount(groupName);

      for (let j = 0; j < motionCount; j++) {
        const motionFileName = this._modelSetting.getMotionFileName(groupName, j);

        // Extract just the filename without path (e.g., "motions/Idle_00.motion3.json" -> "Idle_00")
        const fileNameOnly = motionFileName.split('/').pop()?.replace('.motion3.json', '') || '';

        if (fileNameOnly === cleanFileName) {
          return { group: groupName, index: j };
        }
      }
    }

    return null;
  }

  /**
   * ランダムに選ばれたモーションの再生を開始する。
   * @param group モーショングループ名
   * @param priority 優先度
   * @param onFinishedMotionHandler モーション再生終了時に呼び出されるコールバック関数
   * @return 開始したモーションの識別番号を返す。個別のモーションが終了したか否かを判定するisFinished()の引数で使用する。開始できない時は[-1]
   */
  public startRandomMotion(
    group: string,
    priority: number,
    onFinishedMotionHandler?: FinishedMotionCallback
  ): CubismMotionQueueEntryHandle {
    if (this._modelSetting.getMotionCount(group) == 0) {
      return InvalidMotionQueueEntryHandleValue;
    }

    const no: number = Math.floor(
      Math.random() * this._modelSetting.getMotionCount(group)
    );

    return this.playMotion(group, no, priority, onFinishedMotionHandler);
  }

  /**
   * 引数で指定した表情モーションをセットする
   *
   * @param expressionId 表情モーションのID
   */
  public setExpression(expressionId: string): void {
    const motion: ACubismMotion = this._expressions.getValue(expressionId);

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]expression: [${expressionId}]`);
    }

    if (motion != null) {
      this._expressionManager.startMotionPriority(
        motion,
        false,
        LAppDefine.PriorityForce
      );
    } else {
      if (this._debugMode) {
        LAppPal.printMessage(`[APP]expression[${expressionId}] is null`);
      }
    }
  }

  /**
   * ランダムに選ばれた表情モーションをセットする
   */
  public setRandomExpression(): void {
    if (this._expressions.getSize() == 0) {
      return;
    }

    const no: number = Math.floor(Math.random() * this._expressions.getSize());

    for (let i = 0; i < this._expressions.getSize(); i++) {
      if (i == no) {
        const name: string = this._expressions._keyValues[i].first;
        this.setExpression(name);
        return;
      }
    }
  }

  /**
   * イベントの発火を受け取る
   */
  public motionEventFired(eventValue: csmString): void {
    CubismLogInfo('{0} is fired on LAppModel!!', eventValue.s);
  }

  /**
   * 当たり判定テスト
   * 指定ＩＤの頂点リストから矩形を計算し、座標をが矩形範囲内か判定する。
   *
   * @param hitArenaName  当たり判定をテストする対象のID
   * @param x             判定を行うX座標
   * @param y             判定を行うY座標
   */
  public hitTest(hitArenaName: string, x: number, y: number): boolean {
    // 透明時は当たり判定無し。
    if (this._opacity < 1) {
      return false;
    }

    const count: number = this._modelSetting.getHitAreasCount();

    for (let i = 0; i < count; i++) {
      if (this._modelSetting.getHitAreaName(i) == hitArenaName) {
        const drawId: CubismIdHandle = this._modelSetting.getHitAreaId(i);
        return this.isHit(drawId, x, y);
      }
    }

    return false;
  }

  /**
   * モーションデータをグループ名から一括でロードする。
   * モーションデータの名前は内部でModelSettingから取得する。
   *
   * @param group モーションデータのグループ名
   */
  public preLoadMotionGroup(group: string): void {
    for (let i = 0; i < this._modelSetting.getMotionCount(group); i++) {
      const motionFileName = this._modelSetting.getMotionFileName(group, i);

      // ex) idle_0
      const name = `${group}_${i}`;
      if (this._debugMode) {
        LAppPal.printMessage(
          `[APP]load motion: ${motionFileName} => [${name}]`
        );
      }

      fetch(`${this._modelHomeDir}${motionFileName}`)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else if (response.status >= 400) {
            CubismLogError(
              `Failed to load file ${this._modelHomeDir}${motionFileName}`
            );
            return new ArrayBuffer(0);
          }
        })
        .then(arrayBuffer => {
          const tmpMotion: CubismMotion = this.loadMotion(
            arrayBuffer,
            arrayBuffer.byteLength,
            name
          );

          if (tmpMotion != null) {
            let fadeTime = this._modelSetting.getMotionFadeInTimeValue(
              group,
              i
            );
            if (fadeTime >= 0.0) {
              tmpMotion.setFadeInTime(fadeTime);
            }

            fadeTime = this._modelSetting.getMotionFadeOutTimeValue(group, i);
            if (fadeTime >= 0.0) {
              tmpMotion.setFadeOutTime(fadeTime);
            }
            tmpMotion.setEffectIds(this._eyeBlinkIds, this._lipSyncIds);

            if (this._motions.getValue(name) != null) {
              ACubismMotion.delete(this._motions.getValue(name));
            }

            this._motions.setValue(name, tmpMotion);

            this._motionCount++;
            if (this._motionCount >= this._allMotionCount) {
              this._state = LoadStep.LoadTexture;

              // 全てのモーションを停止する
              this._motionManager.stopAllMotions();

              this._updating = false;
              this._initialized = true;

              this.createRenderer();
              this.setupTextures();
              this.getRenderer().startUp(gl);
            }
          } else {
            // loadMotionできなかった場合はモーションの総数がずれるので1つ減らす
            this._allMotionCount--;
          }
        });
    }
  }

  /**
   * すべてのモーションデータを解放する。
   */
  public releaseMotions(): void {
    this._motions.clear();
  }

  /**
   * 全ての表情データを解放する。
   */
  public releaseExpressions(): void {
    this._expressions.clear();
  }

  /**
   * モデルを描画する処理。モデルを描画する空間のView-Projection行列を渡す。
   */
  public doDraw(): void {
    if (this._model == null) return;

    // キャンバスサイズを渡す
    const viewport: number[] = [0, 0, canvas.width, canvas.height];

    this.getRenderer().setRenderState(frameBuffer, viewport);
    this.getRenderer().drawModel();
  }

  /**
   * モデルを描画する処理。モデルを描画する空間のView-Projection行列を渡す。
   */
  public draw(matrix: CubismMatrix44): void {
    if (this._model == null) {
      return;
    }

    // 各読み込み終了後
    if (this._state == LoadStep.CompleteSetup) {
      matrix.multiplyByMatrix(this._modelMatrix);

      this.getRenderer().setMvpMatrix(matrix);

      this.doDraw();
    }
  }

  public async hasMocConsistencyFromFile() {
    CSM_ASSERT(this._modelSetting.getModelFileName().localeCompare(``));

    // CubismModel
    if (this._modelSetting.getModelFileName() != '') {
      const modelFileName = this._modelSetting.getModelFileName();

      const response = await fetch(`${this._modelHomeDir}${modelFileName}`);
      const arrayBuffer = await response.arrayBuffer();

      this._consistency = CubismMoc.hasMocConsistency(arrayBuffer);

      if (!this._consistency) {
        CubismLogInfo('Inconsistent MOC3.');
      } else {
        CubismLogInfo('Consistent MOC3.');
      }

      return this._consistency;
    } else {
      LAppPal.printMessage('Model data does not exist.');
    }
  }

  /**
   * 自動話しアニメーションを開始する
   * @param speed 話す速度（1秒間の口の開閉回数、デフォルト: 4.0）
   * @param intensity 話す強度（口の開閉幅、0.0-1.0、デフォルト: 0.6）
   */
  public startTalking(speed: number = 4.0, intensity: number = 0.6): void {
    console.log("startTalking called with speed:", speed, "intensity:", intensity);

    if (this._isTalking) {
      console.log("Already talking, ignoring call");
      return; // 既に話している場合は何もしない
    }

    // 現在のリップシンク状態を保存
    this._originalLipSyncState = this._lipsync;
    console.log("Original lip sync state:", this._originalLipSyncState);

    // リップシンクを無効にして、手動制御に切り替え
    this._lipsync = false;

    // 話しアニメーション開始
    this._isTalking = true;
    this._talkingTime = 0.0;
    this._talkingSpeed = Math.max(0.1, speed); // 最小値制限
    this._talkingIntensity = Math.max(0.0, Math.min(1.0, intensity)); // 0.0-1.0にクランプ

    console.log("Talking started - isTalking:", this._isTalking, "speed:", this._talkingSpeed, "intensity:", this._talkingIntensity);
    console.log("Lip sync IDs available:", this._lipSyncIds.getSize());

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]Started talking animation (speed: ${this._talkingSpeed}, intensity: ${this._talkingIntensity})`);
    }
  }

  /**
   * 自動話しアニメーションを停止する
   */
  public endTalking(): void {
    if (!this._isTalking) {
      return; // 話していない場合は何もしない
    }

    // 話しアニメーション停止
    this._isTalking = false;
    this._talkingTime = 0.0;

    // 口を徐々に閉じるアニメーション開始
    this._isClosingMouth = true;
    this._mouthCloseTime = 0.0;

    console.log("Started mouth closing animation");

    if (this._debugMode) {
      LAppPal.printMessage('[APP]Stopping talking animation with smooth mouth close');
    }
  }

  /**
   * 話している状態かどうかを取得する
   * @returns 話している場合はtrue
   */
  public isTalking(): boolean {
    return this._isTalking;
  }

  /**
   * 話しアニメーションの速度を設定する
   * @param speed 新しい速度（1秒間の口の開閉回数）
   */
  public setTalkingSpeed(speed: number): void {
    this._talkingSpeed = Math.max(0.1, speed);
  }

  /**
   * 話しアニメーションの強度を設定する
   * @param intensity 新しい強度（口の開閉幅、0.0-1.0）
   */
  public setTalkingIntensity(intensity: number): void {
    this._talkingIntensity = Math.max(0.0, Math.min(1.0, intensity));
  }

  /**
   * 母音に基づいて口のパラメータを取得する
   * @param vowel 母音 (A, E, I, O, U)
   * @param intensity 基本強度 (0.0-1.0)
   * @returns 口のパラメータオブジェクト
   */
  private getVowelMouthParameters(vowel: string, intensity: number): any {
    // Only apply minimum opening when intensity is above threshold
    const shouldApplyMinimum = intensity > 0.15;
    const minOpening = shouldApplyMinimum ? 0.15 : 0.0; // Smaller minimum, only when actually talking
    const enhancedIntensity = Math.max(minOpening, intensity);

    const params = {
      mouthOpenY: 0.0,
      mouthForm: 0.0,
      mouthWidth: 0.0
    };

    switch (vowel) {
      case 'A':
        // A: 大きく開いた口、やや縦長
        params.mouthOpenY = enhancedIntensity * 0.6 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -0.6; // 縦長
        params.mouthWidth = 0.2;
        break;
      case 'E':
        // E: 横に広がった口、中程度に開く
        params.mouthOpenY = enhancedIntensity *  0.43 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -0.3; // 横長
        params.mouthWidth = 0.6;
        break;
      case 'I':
        // I: 小さく開いた口、横に引っ張られた形
        params.mouthOpenY = enhancedIntensity * 0.43 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -0.35; // 横長
        params.mouthWidth = 0.6;
        break;
      case 'O':
        // O: 丸い形、大きめに開く
        params.mouthOpenY = enhancedIntensity * 0.4 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -1; // やや丸
        params.mouthWidth = -0.2; // 少し窄む
        break;
      case 'U':
        // U: 小さく丸い、但し見えるように開く
        params.mouthOpenY = enhancedIntensity * 0.35 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -0.9; // 丸い
        params.mouthWidth = -0.6; // 窄む
        break;
      default:
        params.mouthOpenY = enhancedIntensity * 0.8 + (shouldApplyMinimum ? 0.1 : 0.0);
        break;
    }

    // Ensure all values are within valid range, allow closing to 0
    params.mouthOpenY = Math.max(0.0, Math.min(1.0, params.mouthOpenY));
    params.mouthForm = Math.max(-1.0, Math.min(1.0, params.mouthForm));
    params.mouthWidth = Math.max(-1.0, Math.min(1.0, params.mouthWidth));

    return params;
  }

  /**
   * 口のパラメータをモデルに適用する
   * @param params 口のパラメータオブジェクト
   */
  private applyMouthParameters(params: any): void {
    // メインの口の開閉
    if (this._lipSyncIds.getSize() > 0) {
      for (let i = 0; i < this._lipSyncIds.getSize(); ++i) {
        this._model.setParameterValueById(this._lipSyncIds.at(i), params.mouthOpenY);
      }
    } else {
      const mouthParamId = CubismFramework.getIdManager().getId("ParamMouthOpenY");
      this._model.setParameterValueById(mouthParamId, params.mouthOpenY);
    }

    // 追加の口の形状パラメータ（モデルが対応している場合）
    try {
      const mouthFormId = CubismFramework.getIdManager().getId("ParamMouthForm");
      this._model.setParameterValueById(mouthFormId, params.mouthForm);
    } catch (e) {
      // パラメータが存在しない場合は無視
    }

    try {
      const mouthWidthId = CubismFramework.getIdManager().getId("ParamMouthWidth");
      this._model.setParameterValueById(mouthWidthId, params.mouthWidth);
    } catch (e) {
      // パラメータが存在しない場合は無視
    }

    // 他の一般的な口パラメータも試す
    try {
      const mouthSizeId = CubismFramework.getIdManager().getId("ParamMouthSize");
      this._model.addParameterValueById(mouthSizeId, params.mouthWidth, 0.6);
    } catch (e) {
      // パラメータが存在しない場合は無視
    }

  }

  /**
   * チャンネル別表情システムのメソッド群
   */

  /**
   * チャンネル別表情を再生する
   * @param channel チャンネル名
   * @param expressionName 表情名
   * @param duration 持続時間（秒、-1で永続）
   */
  public playExpression(channel: string, expressionName: string, duration: number): void {
    if (this._debugMode) {
      LAppPal.printMessage(`[APP]playExpression: channel=${channel}, expression=${expressionName}, duration=${duration}`);
    }

    // 表情データの取得
    const motion: ACubismMotion = this._expressions.getValue(expressionName);
    if (motion == null) {
      if (this._debugMode) {
        LAppPal.printMessage(`[APP]Expression '${expressionName}' not found`);
      }
      return;
    }

    // 既存のチャンネル表情がある場合は停止
    if (this._channelExpressions.isExist(channel)) {
      this.stopChannelExpression(channel);
    }

    // 新しい表情マネージャーを作成
    const manager = new CubismExpressionMotionManager();

    // チャンネル表情状態を作成
    const state: ChannelExpressionState = {
      expressionName: expressionName,
      motion: motion,
      manager: manager,
      duration: duration,
      startTime: this._userTimeSeconds,
      fadingOut: false,
      fadeOutStartTime: 0.0,
      fadeOutDuration: this._defaultFadeDuration
    };

    // チャンネルに登録
    this._channelExpressions.setValue(channel, state);

    // 表情を開始
    manager.startMotionPriority(motion, false, LAppDefine.PriorityForce);

    console.log(`Started expression '${expressionName}' on channel '${channel}' for ${duration === -1 ? 'permanent' : duration + 's'}`);
  }

  /**
   * チャンネル別表情を即座に再生する（フェードなし、初期化用）
   * @param channel チャンネル名
   * @param expressionName 表情名
   */
  public playExpressionInstant(channel: string, expressionName: string): void {
    console.log(`[playExpressionInstant START] channel='${channel}', expression='${expressionName}'`);

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]playExpressionInstant: channel=${channel}, expression=${expressionName}`);
    }

    // 表情データの取得
    const motion: ACubismMotion = this._expressions.getValue(expressionName);
    if (motion == null) {
      // List all available expressions for debugging
      const availableExpressions: string[] = [];
      for (let i = 0; i < this._expressions.getSize(); i++) {
        availableExpressions.push(this._expressions._keyValues[i].first);
      }
      // console.error(`[playExpressionInstant ERROR] Expression '${expressionName}' not found for channel '${channel}'`);
      // console.error(`[playExpressionInstant] Available expressions (${availableExpressions.length}): ${availableExpressions.join(', ')}`);
      if (this._debugMode) {
        LAppPal.printMessage(`[APP]Expression '${expressionName}' not found`);
      }
      return;
    }
    console.log(`[playExpressionInstant] Motion found for '${expressionName}'`);

    // 既存のチャンネル表情がある場合は停止
    if (this._channelExpressions.isExist(channel)) {
      console.log(`[playExpressionInstant] Stopping existing expression on channel '${channel}'`);
      this.stopChannelExpression(channel);
    }

    // 新しい表情マネージャーを作成
    const manager = new CubismExpressionMotionManager();

    // チャンネル表情状態を作成（永続）
    const state: ChannelExpressionState = {
      expressionName: expressionName,
      motion: motion,
      manager: manager,
      duration: -1, // 永続
      startTime: this._userTimeSeconds,
      fadingOut: false,
      fadeOutStartTime: 0.0,
      fadeOutDuration: 0.0 // フェードなし
    };

    // チャンネルに登録
    this._channelExpressions.setValue(channel, state);

    // 表情を即座に開始（フェードイン時間を0に設定）
    motion.setFadeInTime(0.0);
    manager.startMotionPriority(motion, false, LAppDefine.PriorityForce);

    console.log(`[playExpressionInstant] Applied '${expressionName}' on channel '${channel}' - Total channels: ${this._channelExpressions.getSize()}`);
  }

  /**
   * 複数の表情を1つのチャンネルに適用する（レイヤーとして重ねる）
   * @param channel チャンネル名
   * @param expressionNames 表情名の配列
   * @param isInstant trueの場合フェードなし
   */
  public playMultipleExpressions(channel: string, expressionNames: string[], isInstant: boolean = false): void {
    if (this._debugMode) {
      LAppPal.printMessage(`[APP]playMultipleExpressions: channel=${channel}, expressions=[${expressionNames.join(', ')}], instant=${isInstant}`);
    }

    // 既存のチャンネル表情がある場合は停止
    if (this._channelExpressions.isExist(channel)) {
      this.stopChannelExpression(channel);
    }

    // 新しい表情マネージャーを作成
    const manager = new CubismExpressionMotionManager();

    // すべての表情を順番に適用してブレンド
    let combinedMotion: ACubismMotion | null = null;
    const validExpressions: string[] = [];

    for (const expressionName of expressionNames) {
      const motion: ACubismMotion = this._expressions.getValue(expressionName);
      if (motion == null) {
        if (this._debugMode) {
          LAppPal.printMessage(`[APP]Expression '${expressionName}' not found, skipping`);
        }
        continue;
      }

      validExpressions.push(expressionName);

      // 最初のモーションを基準として使用
      if (combinedMotion == null) {
        combinedMotion = motion;
      }

      // フェード設定
      if (isInstant) {
        motion.setFadeInTime(0.0);
      }

      // マネージャーにモーションを追加（重ね合わせ）
      manager.startMotionPriority(motion, true, LAppDefine.PriorityForce);
    }

    if (combinedMotion == null || validExpressions.length === 0) {
      console.warn(`No valid expressions found for channel '${channel}'`);
      return;
    }

    // チャンネル表情状態を作成
    const state: ChannelExpressionState = {
      expressionName: validExpressions.join('+'), // 複数表情を'+'で結合
      motion: combinedMotion,
      manager: manager,
      duration: -1, // 永続
      startTime: this._userTimeSeconds,
      fadingOut: false,
      fadeOutStartTime: 0.0,
      fadeOutDuration: isInstant ? 0.0 : this._defaultFadeDuration
    };

    // チャンネルに登録
    this._channelExpressions.setValue(channel, state);

    console.log(`Applied ${validExpressions.length} layered expressions [${validExpressions.join(', ')}] on channel '${channel}' (instant: ${isInstant})`);
  }

  /**
   * 指定チャンネルの表情を停止する
   * @param channel チャンネル名
   */
  public stopExpression(channel: string): void {
    if (!this._channelExpressions.isExist(channel)) {
      if (this._debugMode) {
        LAppPal.printMessage(`[APP]No expression found on channel '${channel}'`);
      }
      return;
    }

    this.stopChannelExpression(channel);

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]Stopped expression on channel '${channel}'`);
    }
  }

  /**
   * 現在のチャンネル表情名を取得する
   * @param channel チャンネル名
   * @returns 表情名、なければnull
   */
  public getCurrentExpression(channel: string): string | null {
    if (!this._channelExpressions.isExist(channel)) {
      return null;
    }

    const state = this._channelExpressions.getValue(channel);
    return state.fadingOut ? null : state.expressionName;
  }

  /**
   * すべてのアクティブな表情を取得する
   * @returns チャンネル名をキー、表情名を値とするオブジェクト
   */
  public getAllActiveExpressions(): { [channel: string]: string } {
    const result: { [channel: string]: string } = {};

    for (let i = 0; i < this._channelExpressions.getSize(); i++) {
      const channel = this._channelExpressions._keyValues[i].first;
      const state = this._channelExpressions._keyValues[i].second;

      if (!state.fadingOut) {
        result[channel] = state.expressionName;
      }
    }

    return result;
  }

  /**
   * 指定チャンネルをクリアする
   * @param channel チャンネル名
   */
  public clearChannel(channel: string): void {
    this.stopExpression(channel);
  }

  /**
   * すべてのチャンネルをクリアする
   */
  public clearAllExpressions(): void {
    const channels: string[] = [];

    // チャンネル名を収集
    for (let i = 0; i < this._channelExpressions.getSize(); i++) {
      channels.push(this._channelExpressions._keyValues[i].first);
    }

    // 各チャンネルを停止
    for (const channel of channels) {
      this.stopChannelExpression(channel);
    }

    if (this._debugMode) {
      LAppPal.printMessage('[APP]Cleared all channel expressions');
    }
  }

  /**
   * 指定チャンネルの残り時間を取得する
   * @param channel チャンネル名
   * @returns 残り時間（秒）、永続の場合は-1、チャンネルがない場合は0
   */
  public getChannelDuration(channel: string): number {
    if (!this._channelExpressions.isExist(channel)) {
      return 0;
    }

    const state = this._channelExpressions.getValue(channel);
    if (state.duration === -1) {
      return -1; // 永続
    }

    const elapsed = this._userTimeSeconds - state.startTime;
    const remaining = state.duration - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * チャンネル表情システムの更新処理
   * @param deltaTimeSeconds デルタ時間
   */
  private updateChannelExpressions(deltaTimeSeconds: number): void {
    const channelsToRemove: string[] = [];

    // チャンネルの処理を行う（逆順で処理してインデックスの問題を回避）
    for (let i = this._channelExpressions.getSize() - 1; i >= 0; i--) {
      const channel = this._channelExpressions._keyValues[i].first;
      const state = this._channelExpressions._keyValues[i].second;

      // Null check for safety
      if (state == null) {
        channelsToRemove.push(channel);
        continue;
      }

      // フェードアウト中の処理
      if (state.fadingOut) {
        const fadeElapsed = this._userTimeSeconds - state.fadeOutStartTime;
        if (fadeElapsed >= state.fadeOutDuration) {
          // フェードアウト完了、チャンネルを削除
          channelsToRemove.push(channel);
          continue;
        }

        // フェードアウト中の表情更新
        const fadeProgress = fadeElapsed / state.fadeOutDuration;
        const fadeWeight = 1.0 - fadeProgress; // 1.0から0.0へ
        state.manager.updateMotion(this._model, deltaTimeSeconds);
        continue;
      }

      // 通常の表情更新
      state.manager.updateMotion(this._model, deltaTimeSeconds);

      // 時間制限のある表情の期限チェック
      if (state.duration !== -1) {
        const elapsed = this._userTimeSeconds - state.startTime;
        if (elapsed >= state.duration) {
          // 期限切れ、フェードアウト開始
          state.fadingOut = true;
          state.fadeOutStartTime = this._userTimeSeconds;
          console.log(`Expression '${state.expressionName}' on channel '${channel}' expired, starting fade out`);
        }
      }
    }

    // 削除対象のチャンネルをクリーンアップ
    for (const channel of channelsToRemove) {
      // csmMapでは setValue(key, null) で削除する
      this._channelExpressions.setValue(channel, null);
      console.log(`Removed expired expression from channel '${channel}'`);
    }
  }  /**
   * チャンネル表情の内部停止処理
   * @param channel チャンネル名
   */
  private stopChannelExpression(channel: string): void {
    if (!this._channelExpressions.isExist(channel)) {
      return;
    }

    const state = this._channelExpressions.getValue(channel);

    // フェードアウト開始
    if (!state.fadingOut) {
      state.fadingOut = true;
      state.fadeOutStartTime = this._userTimeSeconds;
      console.log(`Starting fade out for expression '${state.expressionName}' on channel '${channel}'`);
    }
  }

  /**
   * コンストラクタ
   */
  public constructor() {
    super();

    this._modelSetting = null;
    this._modelHomeDir = null;
    this._userTimeSeconds = 0.0;

    this._eyeBlinkIds = new csmVector<CubismIdHandle>();
    this._lipSyncIds = new csmVector<CubismIdHandle>();

    this._motions = new csmMap<string, ACubismMotion>();
    this._expressions = new csmMap<string, ACubismMotion>();

    this._hitArea = new csmVector<csmRect>();
    this._userArea = new csmVector<csmRect>();

    this._idParamAngleX = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamAngleX
    );
    this._idParamAngleY = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamAngleY
    );
    this._idParamAngleZ = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamAngleZ
    );
    this._idParamEyeBallX = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamEyeBallX
    );
    this._idParamEyeBallY = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamEyeBallY
    );
    this._idParamBodyAngleX = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamBodyAngleX
    );

    if (LAppDefine.MOCConsistencyValidationEnable) {
      this._mocConsistency = true;
    }

    this._state = LoadStep.LoadAssets;
    this._expressionCount = 0;
    this._textureCount = 0;
    this._motionCount = 0;
    this._allMotionCount = 0;
    this._wavFileHandler = new LAppWavFileHandler();
    this._consistency = false;

    // Initialize talking animation variables
    this._isTalking = false;
    this._isClosingMouth = false;
    this._mouthCloseTime = 0.0;
    this._talkingTime = 0.0;
    this._talkingSpeed = 4.0; // Slower default speed (oscillations per second)
    this._talkingIntensity = 0.6; // More subtle default intensity
    this._originalLipSyncState = false;
    this._currentVowel = 'A';
    this._vowelChangeTime = 0.0;
    this._vowelDuration = 0.2; // Change vowel every 0.2 seconds

    // Initialize channel expression system
    this._channelExpressions = new csmMap<string, ChannelExpressionState>();
    this._defaultFadeDuration = 0.3; // Default fade transition duration

    this._loadedExpressionNames = new Set<string>();
    this._failedExpressionNames = new Map<string, ExpressionLoadFailure>();
    this._expressionFileMap = new Map<string, string>();
    this._priorityExpressionNames = new Set<string>();
    this._priorityExpressionsLoaded = false;
    this._loadStartTime = 0;
    this._priorityLoadStartTime = 0;
    this._baseModelMatrixArray = null;
    this._modelYOffset = LAppDefine.ModelInitialYOffset;
    this._modelYOffsetCurrent = this._modelYOffset;
    this._modelYOffsetStart = this._modelYOffset;
    this._modelYOffsetTarget = this._modelYOffset;
    this._modelYOffsetElapsed = 0;
    this._modelYOffsetDuration = 0;
    this._modelYOffsetAnimating = false;
    this._modelYOffsetDirty = false;
    this._modelXOffset = 0;
    this._modelXOffsetCurrent = 0;
    this._modelXOffsetStart = 0;
    this._modelXOffsetTarget = 0;
    this._modelXOffsetElapsed = 0;
    this._modelXOffsetDuration = 0;
    this._modelXOffsetAnimating = false;
    this._modelXOffsetDirty = false;
  }

  public setModelYOffset(offset: number, durationMs = 0): void {
    this._modelYOffset = offset;
    const durationSeconds = Math.max(0, durationMs) / 1000;

    if (!this._baseModelMatrixArray) {
      this._modelYOffsetCurrent = offset;
      this._modelYOffsetTarget = offset;
      this._modelYOffsetAnimating = false;
      this._modelYOffsetDirty = true;
      return;
    }

    if (durationSeconds <= 0) {
      this._modelYOffsetCurrent = offset;
      this._modelYOffsetTarget = offset;
      this._modelYOffsetAnimating = false;
      this._modelYOffsetDirty = true;
      this._applyModelOffsets();
      return;
    }

    this._modelYOffsetStart = this._modelYOffsetCurrent;
    this._modelYOffsetTarget = offset;
    this._modelYOffsetElapsed = 0;
    this._modelYOffsetDuration = durationSeconds;
    this._modelYOffsetAnimating = true;
  }

  public setModelXOffset(offset: number, durationMs = 0): void {
    this._modelXOffset = offset;
    const durationSeconds = Math.max(0, durationMs) / 1000;

    if (!this._baseModelMatrixArray) {
      this._modelXOffsetCurrent = offset;
      this._modelXOffsetTarget = offset;
      this._modelXOffsetAnimating = false;
      this._modelXOffsetDirty = true;
      return;
    }

    if (durationSeconds <= 0) {
      this._modelXOffsetCurrent = offset;
      this._modelXOffsetTarget = offset;
      this._modelXOffsetAnimating = false;
      this._modelXOffsetDirty = true;
      this._applyModelOffsets();
      return;
    }

    this._modelXOffsetStart = this._modelXOffsetCurrent;
    this._modelXOffsetTarget = offset;
    this._modelXOffsetElapsed = 0;
    this._modelXOffsetDuration = durationSeconds;
    this._modelXOffsetAnimating = true;
  }

  public getModelYOffset(): number {
    return this._modelYOffset;
  }

  public getModelXOffset(): number {
    return this._modelXOffset;
  }

  private _captureBaseModelMatrix(): void {
    if (!this._modelMatrix) {
      return;
    }

    const current = this._modelMatrix.getArray();
    this._baseModelMatrixArray = new Float32Array(current.length);
    this._baseModelMatrixArray.set(current);
    this._modelYOffsetCurrent = this._modelYOffset;
    this._modelYOffsetStart = this._modelYOffsetCurrent;
    this._modelYOffsetTarget = this._modelYOffsetCurrent;
    this._modelYOffsetElapsed = 0;
    this._modelYOffsetDuration = 0;
    this._modelYOffsetAnimating = false;
    this._modelYOffsetDirty = true;
    this._modelXOffsetCurrent = this._modelXOffset;
    this._modelXOffsetStart = this._modelXOffsetCurrent;
    this._modelXOffsetTarget = this._modelXOffsetCurrent;
    this._modelXOffsetElapsed = 0;
    this._modelXOffsetDuration = 0;
    this._modelXOffsetAnimating = false;
    this._modelXOffsetDirty = true;
    this._applyModelOffsets();
  }

  private _applyModelOffsets(): void {
    if (!this._baseModelMatrixArray || !this._modelMatrix) {
      return;
    }

    const adjusted = new Float32Array(this._baseModelMatrixArray.length);
    adjusted.set(this._baseModelMatrixArray);
    adjusted[12] += this._modelXOffsetCurrent;
    adjusted[13] += this._modelYOffsetCurrent;
    this._modelMatrix.setMatrix(adjusted);
    this._modelYOffsetDirty = false;
    this._modelXOffsetDirty = false;
  }

  private _updateModelYOffset(deltaTimeSeconds: number): void {
    if (!this._baseModelMatrixArray) {
      return;
    }

    if (this._modelYOffsetAnimating) {
      this._modelYOffsetElapsed += deltaTimeSeconds;
      const duration = this._modelYOffsetDuration > 0 ? this._modelYOffsetDuration : deltaTimeSeconds;
      let progress = duration > 0 ? this._modelYOffsetElapsed / duration : 1;
      if (progress >= 1) {
        progress = 1;
        this._modelYOffsetAnimating = false;
      }
      const eased = this._easeInOutCubic(progress);
      this._modelYOffsetCurrent =
        this._modelYOffsetStart +
        (this._modelYOffsetTarget - this._modelYOffsetStart) * eased;
      this._applyModelOffsets();
    } else if (this._modelYOffsetDirty) {
      this._modelYOffsetCurrent = this._modelYOffset;
      this._applyModelOffsets();
    }
  }

  private _updateModelXOffset(deltaTimeSeconds: number): void {
    if (!this._baseModelMatrixArray) {
      return;
    }

    if (this._modelXOffsetAnimating) {
      this._modelXOffsetElapsed += deltaTimeSeconds;
      const duration = this._modelXOffsetDuration > 0 ? this._modelXOffsetDuration : deltaTimeSeconds;
      let progress = duration > 0 ? this._modelXOffsetElapsed / duration : 1;
      if (progress >= 1) {
        progress = 1;
        this._modelXOffsetAnimating = false;
      }
      const eased = this._easeInOutCubic(progress);
      this._modelXOffsetCurrent =
        this._modelXOffsetStart +
        (this._modelXOffsetTarget - this._modelXOffsetStart) * eased;
      this._applyModelOffsets();
    } else if (this._modelXOffsetDirty) {
      this._modelXOffsetCurrent = this._modelXOffset;
      this._applyModelOffsets();
    }
  }

  private _easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  _modelSetting: ICubismModelSetting; // モデルセッティング情報
  _modelHomeDir: string; // モデルセッティングが置かれたディレクトリ
  _userTimeSeconds: number; // デルタ時間の積算値[秒]

  _eyeBlinkIds: csmVector<CubismIdHandle>; // モデルに設定された瞬き機能用パラメータID
  _lipSyncIds: csmVector<CubismIdHandle>; // モデルに設定されたリップシンク機能用パラメータID

  _motions: csmMap<string, ACubismMotion>; // 読み込まれているモーションのリスト
  _expressions: csmMap<string, ACubismMotion>; // 読み込まれている表情のリスト

  _hitArea: csmVector<csmRect>;
  _userArea: csmVector<csmRect>;

  _idParamAngleX: CubismIdHandle; // パラメータID: ParamAngleX
  _idParamAngleY: CubismIdHandle; // パラメータID: ParamAngleY
  _idParamAngleZ: CubismIdHandle; // パラメータID: ParamAngleZ
  _idParamEyeBallX: CubismIdHandle; // パラメータID: ParamEyeBallX
  _idParamEyeBallY: CubismIdHandle; // パラメータID: ParamEyeBAllY
  _idParamBodyAngleX: CubismIdHandle; // パラメータID: ParamBodyAngleX

  _state: LoadStep; // 現在のステータス管理用
  _expressionCount: number; // 表情データカウント
  _textureCount: number; // テクスチャカウント
  _motionCount: number; // モーションデータカウント
  _allMotionCount: number; // モーション総数
  _wavFileHandler: LAppWavFileHandler; //wavファイルハンドラ
  _consistency: boolean; // MOC3一貫性チェック管理用

  // Talking animation variables
  private _isTalking: boolean; // 話している状態かどうか
  private _isClosingMouth: boolean; // 口を閉じるアニメーション中かどうか
  private _mouthCloseTime: number; // 口を閉じるアニメーションの経過時間
  private _talkingTime: number; // 話し始めてからの経過時間
  private _talkingSpeed: number; // 話す速度（口の開閉速度）
  private _talkingIntensity: number; // 話す強度（口の開閉幅）
  private _originalLipSyncState: boolean; // 元のリップシンク状態を保存
  private _currentVowel: string; // 現在の母音形状 (A, E, I, O, U)
  private _vowelChangeTime: number; // 次の母音変更までの時間
  private _vowelDuration: number; // 各母音を保持する時間

  // Channel expression system variables
  private _channelExpressions: csmMap<string, ChannelExpressionState>; // チャンネル別表情管理
  private _defaultFadeDuration: number; // デフォルトフェード時間

  private _loadedExpressionNames: Set<string>;
  private _failedExpressionNames: Map<string, ExpressionLoadFailure>;
  private _expressionFileMap: Map<string, string>;
  private _priorityExpressionNames: Set<string>; // Wardrobe expressions to load first
  private _priorityExpressionsLoaded: boolean; // Flag for priority phase completion
  private _loadStartTime: number; // Timestamp when model loading started
  private _priorityLoadStartTime: number; // Timestamp when priority expression loading started
  private _baseModelMatrixArray: Float32Array | null;
  private _modelYOffset: number;
  private _modelYOffsetCurrent: number;
  private _modelYOffsetStart: number;
  private _modelYOffsetTarget: number;
  private _modelYOffsetElapsed: number;
  private _modelYOffsetDuration: number;
  private _modelYOffsetAnimating: boolean;
  private _modelYOffsetDirty: boolean;
  private _modelXOffset: number;
  private _modelXOffsetCurrent: number;
  private _modelXOffsetStart: number;
  private _modelXOffsetTarget: number;
  private _modelXOffsetElapsed: number;
  private _modelXOffsetDuration: number;
  private _modelXOffsetAnimating: boolean;
  private _modelXOffsetDirty: boolean;
}
